/**
 * What a drafted man played for, and played AS, the year he was drafted.
 *
 * Past drafts is 3,756 picks over 23 seasons out of an archive that records a name and nothing
 * else. `build/make-history-clubs.mjs` joins two sources this repo already has — nflverse's
 * per-season rosters (via `box-clubs.json`) and every lineup the league ever set (via
 * `box-scores.json`) — and writes the answer per season, for drafted men only.
 *
 * THE CLUB IS AN ERA FACT AND THE POSITION IS NOT. Reading the club off today's player index
 * would print `LV` beside a 2009 Raider and `LAR` beside a 2010 St. Louis Ram, silently, on the
 * one page whose whole job is to be the record — so it never is. A position does not move with a
 * franchise, so where the archive's own lineups cannot say what a man was, today's index may.
 *
 * NULL IS A REAL ANSWER, as it is in `boxClubs.ts`: about one pick in fourteen has no club here,
 * mostly men who were drafted and never started. The cell draws no chip. A gap is a gap; a wrong
 * chip is the app lying about the record.
 */

import raw from '../data/history-clubs.json';

interface HistoryClubFile {
  version: number;
  source: string;
  built: string;
  clubs: string[];
  pos: string[];
  /** season → normalised name → [club index or -1, position index or -1]. */
  by: Record<string, Record<string, [number, number]>>;
  /**
   * season → `round.pick` → the same pair.
   *
   * ONE ROW PER NAME COULD NOT HOLD TWO MEN (Tim, 8 September: "two steve smiths drafted in 2009
   * and 2010.. one of those is NYG, one is CAR"). A season with two of a name has one row for
   * them in `by`, so the second overwrote the first and the board drew Carolina's Steve Smith
   * over the Giants'. A pick is unique by construction, so this is the map anything holding a
   * pick should ask; `by` remains for the screens that have a name and nothing else.
   */
  byPick?: Record<string, Record<string, [number, number]>>;
}

const FILE = raw as unknown as HistoryClubFile;

/**
 * The generator's key, exactly: parentheticals out first (`Michael (NO) Thomas` is how the
 * archive disambiguated two of them), then punctuation deleted rather than spaced, so `A.J.
 * Brown` and `AJ Brown` are one man, then the suffixes.
 */
export function historyKey(player: string): string {
  return player
    .replace(/\([^)]*\)/g, ' ')
    .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z ]/g, '')
    .replace(/\b(jr|sr|ii|iii|iv|v)\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface HistoryInfo { club: string | null; pos: string | null }

const EMPTY: HistoryInfo = { club: null, pos: null };

const read = (row: [number, number] | undefined): HistoryInfo => (row
  ? {
    club: row[0] >= 0 ? FILE.clubs[row[0]] ?? null : null,
    pos: row[1] >= 0 ? FILE.pos[row[1]] ?? null : null,
  }
  : EMPTY);

export function historyInfoOf(season: number, player: string | undefined | null): HistoryInfo {
  if (!player) return EMPTY;
  return read(FILE.by[String(season)]?.[historyKey(player)]);
}

/**
 * What the man in THIS CELL was, which is the question a board is asking.
 *
 * Falls back to the name where the pick is not in the file — a rebuilt archive whose rounds have
 * shifted, or the seasons this map does not cover — so a caller never has to know which it got.
 */
export function historyInfoOfPick(
  season: number, round: number, pick: number, player?: string | null,
): HistoryInfo {
  const row = FILE.byPick?.[String(season)]?.[`${round}.${pick}`];
  return row ? read(row) : historyInfoOf(season, player);
}

export const historyClubSource = (): string => FILE.source;
export const historyClubBuilt = (): string => FILE.built;
/** How many of a season's picks this file can name a club for — the caveat line reads it. */
export function historyClubCount(season: number): number {
  return Object.values(FILE.by[String(season)] ?? {}).filter(([c]) => c >= 0).length;
}
